import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Video, VideoOff, Monitor, PhoneOff, MessageSquare, Users, Copy, Shield, Headphones, Send, X, Settings, Maximize2, Minimize2, Volume2 } from 'lucide-react';
import { MeetingConnection } from './MeetingConnection';
import { getMeetingsWsUrl } from './meetConfig';
import { meetingLink, copyText } from './meetApi';
import { attachMediaPlayback } from './mediaPlayback';
function Media({
  stream,
  muted,
  volume = 1,
  className
}) {
  const ref = useRef(null);
  const playback = useRef(null);
  const [blocked, setBlocked] = useState(null);
  useEffect(() => {
    const controller = attachMediaPlayback(ref.current, stream, muted, setBlocked);
    playback.current = controller;
    return () => controller.dispose();
  }, [stream, muted]);
  useEffect(() => {
    ref.current.volume = volume;
  }, [volume]);
  return <><video ref={ref} autoPlay playsInline muted={muted} className={className} />{blocked && <button type="button" className="meet-play" onClick={() => playback.current?.play()}>{blocked === 'sound' ? 'Включить звук собеседника' : 'Запустить видео и звук'}</button>}</>;
}

function LocalCameraPreview({ stream }) {
  const bounds = useRef(null);
  const drag = useRef(null);
  const [position, setPosition] = useState({ x: 1, y: 1 });
  const [dragging, setDragging] = useState(false);
  const finish = event => {
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const start = event => {
    if (!event.isPrimary || event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    drag.current = { id: event.pointerId, offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.focus({ preventScroll: true });
    setDragging(true);
    event.preventDefault();
    event.stopPropagation();
  };
  const move = event => {
    if (drag.current?.id !== event.pointerId) return;
    const area = bounds.current.getBoundingClientRect();
    const rect = event.currentTarget.getBoundingClientRect();
    const maxX = Math.max(0, area.width - rect.width);
    const maxY = Math.max(0, area.height - rect.height);
    const clamp = value => Math.max(0, Math.min(1, value));
    setPosition({
      x: maxX ? clamp((event.clientX - area.left - drag.current.offsetX) / maxX) : 0,
      y: maxY ? clamp((event.clientY - area.top - drag.current.offsetY) / maxY) : 0
    });
    event.preventDefault();
    event.stopPropagation();
  };
  const keyboardMove = event => {
    const direction = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
    if (!direction) return;
    event.preventDefault();
    event.stopPropagation();
    setPosition(previous => ({
      x: Math.max(0, Math.min(1, previous.x + direction[0] * 0.05)),
      y: Math.max(0, Math.min(1, previous.y + direction[1] * 0.05))
    }));
  };
  // Relative coordinates keep the preview inside after rotation or panel resize.
  return <div ref={bounds} className="meet-local-preview-bounds">
    <div className={`meet-local-preview ${dragging ? 'is-dragging' : ''}`} role="group" tabIndex={0}
      aria-label="Ваша камера. Перетащите мышью или пальцем; используйте стрелки на клавиатуре."
      style={{ left: `${position.x * 100}%`, top: `${position.y * 100}%`, transform: `translate(${-position.x * 100}%, ${-position.y * 100}%)` }}
      onPointerDown={start} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish} onKeyDown={keyboardMove}>
      <Media stream={stream} muted className="meet-mirror" />
      <span className="meet-local-preview-label">Вы</span>
    </div>
  </div>;
}

function Tile({
  name,
  stream,
  media = {},
  local,
  volume,
  status,
  screen,
  onScreenVolume,
  localPreview
}) {
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreen) return undefined;
    const close = event => {
      if (event.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [fullscreen]);
  const toggleFullscreen = event => {
    const stage = event.currentTarget.closest('.meet-stage');
    if (stage) stage.scrollTop = 0;
    setFullscreen(value => !value);
  };

  return <article onContextMenu={screen && onScreenVolume ? onScreenVolume : undefined} className={`meet-tile ${fullscreen ? 'meet-tile-expanded' : ''} ${screen ? 'meet-screen-tile' : ''}`}><Media stream={stream} muted={!!local} volume={volume} className={`${local && !screen ? 'meet-mirror' : ''} ${!(screen || media.video) ? 'meet-video-hidden' : ''}`} />{!screen && !media.video && <div className="meet-avatar">{name.slice(0, 1).toUpperCase()}</div>}<div className="meet-tile-label">{!screen && (media.audio ? <Mic size={14} /> : <MicOff size={14} />)} {name}{local && ' (вы)'}{screen && ' · экран'}</div>{status && status !== 'connected' && <span className="meet-tile-status">{status === 'failed' ? 'Нет связи · попробуйте войти заново' : 'Соединяем…'}</span>}<>{screen && onScreenVolume && <button type="button" className="meet-screen-volume-button" aria-label={`Громкость демонстрации ${name}`} title="Громкость демонстрации" onClick={onScreenVolume}><Volume2 size={18} /></button>}</><button type="button" className="meet-fullscreen" aria-label={fullscreen ? 'Уменьшить видео' : 'Увеличить видео'} aria-pressed={fullscreen} onClick={toggleFullscreen}>{fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button>{fullscreen && localPreview?.media.video && localPreview.stream && <LocalCameraPreview stream={localPreview.stream} />}</article>;
}
export default function MeetRoom({
  session,
  onExit
}) {
  const connection = useRef(null);
  const alive = useRef(true);
  const [peers, setPeers] = useState([]);
  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [local, setLocal] = useState({
    stream: null,
    screen: null,
    media: {
      audio: false,
      video: false,
      screen: false
    }
  });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [deafened, setDeafened] = useState(false);
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const noiseSupported = !!navigator.mediaDevices?.getSupportedConstraints?.().noiseSuppression;
  const deviceBusy = useRef(false);
  const [volumes, setVolumes] = useState({});
  const [screenVolumes, setScreenVolumes] = useState({});
  const [screenMenu, setScreenMenu] = useState(null);
  const screenMenuRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState({
    audio: '',
    video: ''
  });
  const [settings, setSettings] = useState(false);
  const chatEnd = useRef(null);
  useEffect(() => {
    alive.current = true;
    const c = new MeetingConnection(session, getMeetingsWsUrl(), {
      peers: setPeers,
      error: setError,
      connected: () => setStatus('connected'),
      disconnected: reason => {
        setStatus('disconnected');
        setError(reason);
        setLocal({
          stream: null,
          screen: null,
          media: {}
        });
      },
      chat: message => setMessages(prev => [...prev.slice(-199), message])
    });
    connection.current = c;
    const leave = () => c.close();
    window.addEventListener('pagehide', leave);
    return () => {
      alive.current = false;
      window.removeEventListener('pagehide', leave);
      c.close();
    };
  }, [session]);
  useEffect(() => {
    chatEnd.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, tab]);
  useEffect(() => {
    if (!screenMenu) return undefined;
    screenMenuRef.current?.querySelector('input')?.focus({ preventScroll: true });
    const dismiss = event => {
      if (!screenMenuRef.current?.contains(event.target)) setScreenMenu(null);
    };
    const escape = event => { if (event.key === 'Escape') setScreenMenu(null); };
    const close = () => setScreenMenu(null);
    window.addEventListener('pointerdown', dismiss);
    window.addEventListener('keydown', escape);
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('pointerdown', dismiss);
      window.removeEventListener('keydown', escape);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, [screenMenu]);
  useEffect(() => {
    if (screenMenu && !peers.some(peer => peer.id === screenMenu.peerId && peer.media?.screen)) setScreenMenu(null);
  }, [peers, screenMenu]);
  function openScreenVolume(event, peerId) {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    setScreenMenu({
      peerId,
      x: Math.max(8, Math.min(event.clientX || rect.left, window.innerWidth - 276)),
      y: Math.max(8, Math.min(event.clientY || rect.bottom, window.innerHeight - 180))
    });
  }
  function screenVolumeControl(peer) {
    return <label className="meet-screen-volume">Демонстрация · {Math.round((screenVolumes[peer.id] ?? 1) * 100)}%
      <input type="range" min="0" max="1" step="0.05" aria-label={`Громкость демонстрации ${peer.name}`} value={screenVolumes[peer.id] ?? 1}
        onChange={event => setScreenVolumes(previous => ({ ...previous, [peer.id]: Number(event.target.value) }))} />
      {peer.media?.screenAudio === false && <small>Демонстрация передаётся без звука.</small>}
    </label>;
  }
  function refreshLocal() {
    if (!alive.current) return;
    const tracks = connection.current.tracks;
    setLocal({
      stream: new MediaStream(tracks.slice(0, 2).filter(Boolean)),
      screen: tracks[2] ? new MediaStream(tracks.slice(2, 4).filter(Boolean)) : null,
      media: {
        audio: !!tracks[0],
        video: !!tracks[1],
        screen: !!tracks[2],
        screenAudio: !!tracks[2] && !!tracks[3]
      }
    });
  }
  async function media(slot, deviceId) {
    if (deviceBusy.current) return;
    deviceBusy.current = true;
    setBusy(true);
    setError('');
    const c = connection.current;
    try {
      if (c.tracks[slot] && deviceId === undefined) {
        if (slot === 2) await c.setScreen(null);
        else await c.setTrack(slot, null);
      } else {
        const kind = slot === 0 ? 'audio' : 'video';
        const device = deviceId === undefined ? selected[kind] : deviceId;
        const constraints = slot === 0 ? {
          echoCancellation: true,
          noiseSuppression,
          ...(device ? {
            deviceId: {
              exact: device
            }
          } : {})
        } : {
          width: {
            ideal: 1280
          },
          height: {
            ideal: 720
          },
          ...(device ? {
            deviceId: {
              exact: device
            }
          } : {})
        };
        const stream = slot === 2 ? await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
          systemAudio: 'include',
          selfBrowserSurface: 'exclude'
        }) : await navigator.mediaDevices.getUserMedia({
          [kind]: constraints
        });
        const track = slot === 0 ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
        if (!alive.current || c.closed) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        if (!track) {
          stream.getTracks().forEach(t => t.stop());
          throw new Error('Capture returned no requested track');
        }
        track.onended = async () => {
          if (c.tracks[slot] === track) {
            if (slot === 2) await c.setScreen(null);
            else await c.setTrack(slot, null);
            refreshLocal();
          }
        };
        if (slot === 2) {
          const audio = stream.getAudioTracks()[0];
          if (audio) audio.onended = async () => {
            if (c.tracks[3] === audio) { await c.setTrack(3, null); refreshLocal(); }
          };
          await c.setScreen(stream);
          if (!audio) setNotice('Экран передаётся без звука. Чтобы поделиться звуком, выберите поддерживаемый источник и включите передачу аудио в окне браузера.');
          else setNotice('Демонстрация со звуком включена.');
        } else await c.setTrack(slot, track);
        if (slot === 0) {
          // Explicitly turning the microphone on also exits the silent mode.
          setDeafened(false);
          const applied = track.getSettings?.().noiseSuppression;
          if (typeof applied === 'boolean') setNoiseSuppression(applied);
        }
        if (navigator.mediaDevices.enumerateDevices) setDevices(await navigator.mediaDevices.enumerateDevices());
      }
      refreshLocal();
    } catch (e) {
      if (alive.current) setError(e.name === 'NotAllowedError' ? 'Доступ не предоставлен. Разрешите устройство в браузере и попробуйте снова.' : 'Устройство недоступно. Проверьте подключение и настройки браузера.');
    } finally {
      deviceBusy.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function toggleDeafened() {
    if (deviceBusy.current) return;
    if (deafened) {
      // Hearing can be restored without unexpectedly reopening the microphone.
      setDeafened(false);
      return;
    }
    const c = connection.current;
    setDeafened(true);
    deviceBusy.current = true;
    setBusy(true);
    try {
      if (c.tracks[0]) {
        // Silence immediately, including while replaceTrack waits for peers.
        c.tracks[0].enabled = false;
        await c.setTrack(0, null);
      }
      refreshLocal();
    } catch {
      if (alive.current) setError('Не удалось обновить микрофон. Проверьте его состояние.');
    } finally {
      deviceBusy.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function toggleNoiseSuppression() {
    if (deviceBusy.current || !noiseSupported) return;
    const next = !noiseSuppression;
    const c = connection.current;
    const track = c.tracks[0];
    deviceBusy.current = true;
    setBusy(true);
    try {
      if (track) {
        await track.applyConstraints({
          ...track.getConstraints?.(),
          echoCancellation: true,
          noiseSuppression: { exact: next }
        });
      }
      if (alive.current && !c.closed) {
        const applied = track?.getSettings?.().noiseSuppression;
        setNoiseSuppression(typeof applied === 'boolean' ? applied : next);
      }
    } catch {
      if (alive.current) setError('Браузер или микрофон не позволяет переключить шумоподавление.');
    } finally {
      deviceBusy.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const leave = () => {
    connection.current.close();
    onExit();
  };
  const copy = async () => {
    try {
      await copyText(meetingLink(session.code));
      setNotice('Ссылка скопирована');
    } catch {
      setError('Не удалось скопировать ссылку. Ссылка доступна в настройках.');
    }
  };
  const sendChat = e => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    const delivered = connection.current.chat(text);
    if (!delivered) {
      setError('Сообщение не отправлено: дождитесь соединения с участником.');
      return;
    }
    setMessages(prev => [...prev.slice(-199), {
      id: `${Date.now()}-self`,
      name: `${session.name} (вы)`,
      text,
      at: Date.now(),
      delivered
    }]);
    setDraft('');
  };
  return <div className="meet-app meet-call">
        <aside className="meet-rail"><div className="meet-rail-logo"><Video /></div><button className={tab === 'people' ? 'active' : ''} title="Участники" aria-label="Участники" onClick={() => setTab(tab === 'people' ? null : 'people')}><Users /></button><button className={tab === 'chat' ? 'active' : ''} title="Чат" aria-label="Чат" onClick={() => setTab(tab === 'chat' ? null : 'chat')}><MessageSquare /></button><div className="meet-rail-bottom"><Shield size={22} /></div></aside>
        <div className="meet-call-main"><header className="meet-call-header"><div><h1>{session.title}</h1><span className="meet-call-meta"><i className={status === 'connected' ? 'online' : ''} />{status === 'connected' ? 'Вы в комнате' : status === 'connecting' ? 'Подключение…' : 'Соединение закрыто'} · {peers.length + 1}/6 участников</span></div>{!session.privateConsultation && <button onClick={copy}><Copy size={16} /><span>Пригласить</span></button>}</header>
            {error && <div role="alert" className="meet-error">{error}<button aria-label="Закрыть сообщение" onClick={() => setError('')}><X size={16} /></button></div>}{notice && <div role="status" className="meet-notice">{notice}</div>}
            {status === 'disconnected' ? <div className="meet-disconnected"><PhoneOff size={40} /><h2>Вы вышли из разговора</h2><button className="meet-primary" onClick={leave}>Вернуться к входу</button></div> : <div className="meet-stage">
              <div className="meet-stage-content">
                {(local.media.screen || peers.some(p => p.media?.screen)) && <div className="meet-screens">{local.media.screen && <Tile name={session.name} stream={local.screen} local screen />}{peers.filter(p => p.media?.screen).map(p => <Tile key={`${p.id}-screen`} name={p.name} stream={p.screenStream} screen status={p.status} volume={deafened ? 0 : screenVolumes[p.id] ?? 1} onScreenVolume={event => openScreenVolume(event, p.id)} />)}</div>}
                <div className={`meet-video-grid ${peers.length === 0 ? 'meet-solo' : ''}`}><Tile name={session.name} stream={local.stream} media={local.media} local />{peers.map(p => <Tile key={p.id} name={p.name} stream={p.stream} media={p.media} status={p.status} localPreview={local} volume={deafened ? 0 : volumes[p.id] ?? 1} />)}</div>
                {!peers.length && <div className="meet-waiting"><h2>Всё готово для разговора</h2><p>{session.privateConsultation ? "Собеседник войдёт со страницы консультации." : "Отправьте ссылку собеседнику. Он появится здесь после входа."}</p>{!session.privateConsultation && <button onClick={copy}><Copy size={16} /> Скопировать приглашение</button>}</div>}
              </div>
            </div>}
            {screenMenu && peers.find(peer => peer.id === screenMenu.peerId && peer.media?.screen) && <section ref={screenMenuRef} className="meet-screen-menu" role="dialog" aria-label="Звук демонстрации" style={{ left: screenMenu.x, top: screenMenu.y }}>
              <header><b>Звук демонстрации</b><button type="button" aria-label="Закрыть громкость демонстрации" onClick={() => setScreenMenu(null)}><X size={16} /></button></header>
              {screenVolumeControl(peers.find(peer => peer.id === screenMenu.peerId))}
              {deafened && <small>Звук участников сейчас выключен.</small>}
            </section>}
            {settings && <section className="meet-device-settings"><div className="meet-section-heading"><h3>Устройства и приглашение</h3><button onClick={() => setSettings(false)} aria-label="Закрыть настройки"><X size={16} /></button></div>{['audio', 'video'].map((kind, i) => <label key={kind}>{i === 0 ? 'Микрофон' : 'Камера'}<select value={selected[kind]} onChange={e => {
            const value = e.target.value;
            setSelected(prev => ({
              ...prev,
              [kind]: value
            }));
            if (local.media[kind]) media(i, value);
          }} disabled={busy}><option value="">По умолчанию</option>{devices.filter(d => d.kind === `${kind}input`).map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Устройство'}</option>)}</select></label>)}<button type="button" className={`meet-noise-toggle ${noiseSupported && noiseSuppression ? 'active' : ''}`} aria-label="Шумоподавление" aria-pressed={noiseSupported && noiseSuppression} disabled={busy || !noiseSupported || status !== 'connected'} onClick={toggleNoiseSuppression}>Шумоподавление: {noiseSupported ? noiseSuppression ? 'включено' : 'выключено' : 'недоступно'}</button><small>Список устройств появится после разрешения доступа.</small>{!session.privateConsultation && <input aria-label="Ссылка приглашения" value={meetingLink(session.code)} readOnly />}<p>В окне выбора демонстрации включите передачу звука. Его доступность зависит от браузера и выбранного источника.</p></section>}
            <footer className="meet-controls"><span className="meet-controls-caption"><Shield size={14} /> Без записи</span><div className="meet-control-buttons"><button disabled={busy || status !== 'connected'} className={!local.media.audio ? 'off' : ''} aria-label={local.media.audio ? 'Выключить микрофон' : 'Включить микрофон'} title="Микрофон" onClick={() => media(0)}>{local.media.audio ? <Mic /> : <MicOff />}</button><button disabled={busy || status !== 'connected'} className={!local.media.video ? 'off' : ''} aria-label={local.media.video ? 'Выключить камеру' : 'Включить камеру'} title="Камера" onClick={() => media(1)}>{local.media.video ? <Video /> : <VideoOff />}</button><button disabled={busy || status !== 'connected'} className={local.media.screen ? 'active' : ''} aria-label={local.media.screen ? 'Остановить демонстрацию' : 'Показать экран'} title="Демонстрация экрана" onClick={() => media(2)}><Monitor /></button><button disabled={busy || status !== 'connected'} className={deafened ? 'off' : ''} aria-label={deafened ? 'Включить звук участников' : 'Выключить звук и микрофон'} aria-pressed={deafened} title={deafened ? 'Включить звук участников; микрофон останется выключен' : 'Выключить звук и микрофон'} onClick={toggleDeafened}>{deafened ? <span className="meet-headphones-off" aria-hidden="true"><Headphones /><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3 21 21" /></svg></span> : <Headphones />}</button><button title="Настройки" aria-label="Настройки устройств" onClick={() => setSettings(!settings)}><Settings size={20} /></button><button className="meet-hangup" title="Выйти" aria-label="Выйти из встречи" onClick={leave}><PhoneOff /></button></div>{session.owner && <button className="meet-end" disabled={status !== 'connected'} onClick={() => {
          if (window.confirm('Завершить разговор для всех? Ссылку можно будет использовать снова.')) connection.current.send({
            type: 'end'
          });
        }}>Завершить для всех</button>}</footer>
        </div>
        {tab && <aside className="meet-sidebar"><header><h2>{tab === 'chat' ? 'Чат встречи' : 'Участники'} <span>{tab === 'people' ? peers.length + 1 : ''}</span></h2><button aria-label="Скрыть панель" onClick={() => setTab(null)}><X size={18} /></button></header>{tab === 'chat' ? <><p className="meet-chat-note">Сообщения доступны только подключённым участникам. История не сохраняется после выхода.</p><div className="meet-messages">{!messages.length && <div className="meet-chat-empty"><MessageSquare size={32} /><h3>Разговор продолжается здесь</h3><p>Поделитесь мыслью или отправьте ссылку.</p></div>}{messages.map(m => <div className="meet-message" key={m.id}><div><b>{m.name}</b><time>{new Date(m.at).toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}</time></div><p>{m.text}</p>{m.delivered != null && <small>Отправлено участникам: {m.delivered}</small>}</div>)}<div ref={chatEnd} /></div><form className="meet-chat-form" onSubmit={sendChat}><input aria-label="Сообщение" placeholder="Написать сообщение…" maxLength={2000} value={draft} onChange={e => setDraft(e.target.value)} disabled={status !== 'connected'} /><button aria-label="Отправить сообщение" disabled={!draft.trim() || status !== 'connected'}><Send size={19} /></button></form></> : <div className="meet-people"><div className="meet-person"><b>{session.name} (вы)</b><small>{session.owner ? 'Организатор' : 'Участник'}</small></div>{peers.map(p => <div className="meet-person" key={p.id}><b>{p.name}</b><small>{p.owner ? 'Организатор' : 'Участник'} · {p.media?.audio ? 'Микрофон включён' : 'Микрофон выключен'}</small><label>Микрофон · {Math.round((volumes[p.id] ?? 1) * 100)}%<input aria-label={`Громкость ${p.name}`} type="range" min="0" max="1" step="0.05" value={volumes[p.id] ?? 1} onChange={e => setVolumes(prev => ({
              ...prev,
              [p.id]: Number(e.target.value)
            }))} /></label>{p.media?.screen && screenVolumeControl(p)}{session.owner && !p.owner && <button onClick={() => {
            if (window.confirm(`Удалить участника ${p.name}? Для запрета повторного входа смените пароль встречи.`)) connection.current.send({
              type: 'kick',
              to: p.id
            });
          }}>Удалить из звонка</button>}</div>)}</div>}</aside>}
    </div>;
}
